import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Bell, BellOff, Trophy, Package, Megaphone, MapPin, User, Wallet, LogOut, Link2, Unlink, BookMarked, Plus, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import XamanConnectDialog from '../components/XamanConnectDialog';
import { toast } from 'sonner';

const tg = () => window.Telegram?.WebApp;

function Settings() {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading, refreshUser, logout } = useAuth();
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profileForm, setProfileForm] = useState({ location: '', bio: '', telegram_username: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [blocks, setBlocks] = useState([]);
  const [unlinking, setUnlinking] = useState(false);
  const [confirmUnlink, setConfirmUnlink] = useState(false);
  const [newWallet, setNewWallet] = useState('');
  const [linking, setLinking] = useState(false);
  const [showXamanDialogSettings, setShowXamanDialogSettings] = useState(false);

  // Address Book — saved trusted withdrawal destinations
  const [addresses, setAddresses] = useState([]);
  const [addrLabel, setAddrLabel] = useState('');
  const [addrValue, setAddrValue] = useState('');
  const [addrSaving, setAddrSaving] = useState(false);

  const fetchAddresses = async () => {
    try {
      const res = await axios.get(`${API}/user/address-book`);
      setAddresses(res.data.items || []);
    } catch { /* ignore */ }
  };

  const saveAddress = async () => {
    const label = addrLabel.trim();
    const address = addrValue.trim();
    if (!label) { toast.error('Add a label (e.g. "Cold Wallet")'); return; }
    if (!address.startsWith('r') || address.length < 25) { toast.error('Enter a valid XRPL address'); return; }
    setAddrSaving(true);
    try {
      await axios.post(`${API}/user/address-book`, { label, address });
      toast.success(`Saved "${label}"`);
      setAddrLabel(''); setAddrValue('');
      await fetchAddresses();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setAddrSaving(false); }
  };

  const deleteAddress = async (entry_id, label) => {
    try {
      await axios.delete(`${API}/user/address-book/${entry_id}`);
      toast.success(`Removed "${label}"`);
      await fetchAddresses();
    } catch {
      toast.error('Failed to remove');
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { navigate('/'); return; }
    fetchPrefs();
    fetchBlocks();
    fetchAddresses();
    setProfileForm({
      location: user?.location || '',
      bio: user?.bio || '',
      telegram_username: user?.telegram_username || '',
    });
    const telegram = tg();
    if (telegram) {
      telegram.BackButton.show();
      telegram.BackButton.onClick(() => navigate('/'));
    }
    return () => { if (tg()) { tg().BackButton.hide(); } };
  }, [isAuthenticated, authLoading, user]);

  const fetchBlocks = async () => {
    try {
      const res = await axios.get(`${API}/users/blocks`);
      setBlocks(res.data.blocks || []);
    } catch { /* ignore */ }
  };

  const handleUnblock = async (uid, username) => {
    try {
      await axios.delete(`${API}/users/block/${uid}`);
      toast.success(`Unblocked @${username}`);
      fetchBlocks();
    } catch {
      toast.error('Failed to unblock');
    }
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await axios.put(`${API}/user/profile`, profileForm);
      toast.success('Profile updated');
      if (refreshUser) await refreshUser();
    } catch {
      toast.error('Failed to update profile');
    } finally { setSavingProfile(false); }
  };

  const fetchPrefs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/user/notifications`);
      setPrefs(response.data.preferences);
    } catch (e) {
      console.error('Error fetching prefs:', e);
    } finally {
      setLoading(false);
    }
  };

  const togglePref = async (key) => {
    if (!prefs) return;
    const newValue = !prefs[key];
    setSaving(true);
    try {
      const response = await axios.put(`${API}/user/notifications`, { [key]: newValue });
      setPrefs(response.data.preferences);
      toast.success(`${key.replace('_', ' ')} ${newValue ? 'enabled' : 'disabled'}`);
    } catch (e) {
      toast.error('Failed to update');
    } finally {
      setSaving(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500 mx-auto"></div>
      </div>
    );
  }

  const toggleItems = [
    { key: 'draw_reminders', icon: Bell, label: 'Draw Reminders', desc: '30-min and 5-min alerts before each draw', color: 'text-orange-400' },
    { key: 'win_alerts', icon: Trophy, label: 'Win Alerts', desc: 'Notifications when you win a prize pool', color: 'text-yellow-400' },
    { key: 'escrow_updates', icon: Package, label: 'Escrow Updates', desc: 'Shipping, receipt, and auto-release alerts', color: 'text-blue-400' },
    { key: 'marketing', icon: Megaphone, label: 'Announcements', desc: 'New rooms, promotions, and platform updates', color: 'text-purple-400' },
  ];

  return (
    <div className="min-h-screen pb-6">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-slate-700/50 backdrop-blur-lg bg-slate-900/80">
        <div className="px-4 py-3 flex items-center gap-3">
          <button data-testid="back-button" onClick={() => navigate('/')} className="text-slate-400 hover:text-white p-1">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-base font-bold text-white leading-tight">Notifications</h1>
            <p className="text-[11px] text-slate-500">Manage your alerts</p>
          </div>
        </div>
      </header>

      {/* Public Profile */}
      <div className="px-4 pt-5 space-y-3">
        <div className="flex items-center gap-2">
          <User className="w-4 h-4 text-purple-400" />
          <p className="text-xs font-bold text-white uppercase tracking-wide">Public Profile</p>
        </div>
        <div className="bg-slate-800/40 border border-slate-700/40 rounded-xl p-4 space-y-2.5">
          <div>
            <label className="block text-[10px] text-slate-400 uppercase tracking-wide mb-1">Location (city, region)</label>
            <Input
              data-testid="profile-location-input"
              placeholder="e.g. Mesa, Arizona (optional)"
              value={profileForm.location}
              onChange={(e) => setProfileForm(f => ({ ...f, location: e.target.value }))}
              className="bg-slate-900 border-slate-700 text-white h-9 text-sm"
            />
          </div>
          <div>
            <label className="block text-[10px] text-slate-400 uppercase tracking-wide mb-1">Bio</label>
            <Input
              data-testid="profile-bio-input"
              placeholder="Short tagline (optional, 200 chars max)"
              value={profileForm.bio}
              onChange={(e) => setProfileForm(f => ({ ...f, bio: e.target.value }))}
              className="bg-slate-900 border-slate-700 text-white h-9 text-sm"
            />
          </div>
          <div>
            <label className="block text-[10px] text-slate-400 uppercase tracking-wide mb-1">Telegram username (for buyer chat)</label>
            <Input
              data-testid="profile-telegram-input"
              placeholder="@your_telegram (without @)"
              value={profileForm.telegram_username}
              onChange={(e) => setProfileForm(f => ({ ...f, telegram_username: e.target.value.replace(/^@/, '') }))}
              className="bg-slate-900 border-slate-700 text-white h-9 text-sm"
            />
            <p className="text-[9px] text-slate-500 mt-1">Buyers can chat with you about listings. Leave empty to disable chat.</p>
          </div>
          <Button
            data-testid="save-profile-btn"
            onClick={saveProfile}
            disabled={savingProfile}
            size="sm"
            className="w-full bg-purple-600 hover:bg-purple-700 h-9 text-xs"
          >
            <MapPin className="w-3.5 h-3.5 mr-1" /> {savingProfile ? 'Saving...' : 'Save Profile'}
          </Button>
          {user?.username && (
            <button
              data-testid="view-public-profile-btn"
              onClick={() => navigate(`/profile/${encodeURIComponent(user.username)}`)}
              className="w-full text-[10px] text-indigo-300 hover:text-indigo-200 underline pt-1"
            >
              View my public profile →
            </button>
          )}
        </div>
      </div>

      {/* Wallet */}
      <div className="px-4 pt-5 space-y-3">
        <div className="flex items-center gap-2">
          <Wallet className="w-4 h-4 text-indigo-400" />
          <p className="text-xs font-bold text-white uppercase tracking-wide">XRPL Wallet</p>
        </div>
        <div className="bg-slate-800/40 border border-slate-700/40 rounded-xl p-4 space-y-3">
          {user?.xrpl_address ? (
            <>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-1">Linked Wallet</p>
                <code data-testid="linked-wallet-address" className="text-[11px] text-white font-mono break-all bg-slate-900/60 rounded px-2 py-1.5 block">
                  {user.xrpl_address}
                </code>
              </div>
              {!confirmUnlink ? (
                <Button
                  data-testid="unlink-wallet-btn"
                  onClick={() => setConfirmUnlink(true)}
                  size="sm"
                  variant="outline"
                  className="w-full border-amber-500/40 text-amber-300 hover:bg-amber-500/10 h-9 text-xs"
                >
                  <Unlink className="w-3.5 h-3.5 mr-1.5" /> Unlink Wallet
                </Button>
              ) : (
                <div className="space-y-2 bg-amber-500/5 border border-amber-500/30 rounded-lg p-3">
                  <p className="text-[11px] text-amber-200">
                    Unlinking won't touch your balance or NFTs on-chain — it just disconnects this address from your account so you can link a different one.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      data-testid="confirm-unlink-btn"
                      onClick={async () => {
                        setUnlinking(true);
                        try {
                          await axios.post(`${API}/auth/unlink-wallet`);
                          toast.success('Wallet unlinked');
                          setConfirmUnlink(false);
                          if (refreshUser) await refreshUser();
                        } catch (e) {
                          toast.error(e.response?.data?.detail || 'Failed to unlink');
                        } finally { setUnlinking(false); }
                      }}
                      disabled={unlinking}
                      size="sm"
                      className="flex-1 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold h-8 text-xs"
                    >
                      {unlinking ? 'Unlinking…' : 'Yes, Unlink'}
                    </Button>
                    <Button
                      data-testid="cancel-unlink-btn"
                      onClick={() => setConfirmUnlink(false)}
                      size="sm"
                      variant="ghost"
                      className="flex-1 text-slate-400 h-8 text-xs"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <p className="text-[11px] text-slate-400">
                No wallet linked yet. Connect Xaman with one tap, or paste a classic address.
              </p>

              {/* PRIMARY: Xaman one-tap */}
              <Button
                data-testid="settings-xaman-connect-btn"
                onClick={() => setShowXamanDialogSettings(true)}
                className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold h-10 text-sm"
              >
                <Wallet className="w-3.5 h-3.5 mr-1.5" /> Connect with Xaman
              </Button>

              <div className="flex items-center gap-2 py-0.5">
                <div className="flex-1 h-px bg-slate-700/50" />
                <span className="text-[9px] text-slate-500 uppercase tracking-wider">Or paste manually</span>
                <div className="flex-1 h-px bg-slate-700/50" />
              </div>

              <Input
                data-testid="settings-wallet-input"
                placeholder="rXXXXXX..."
                value={newWallet}
                onChange={(e) => setNewWallet(e.target.value.trim())}
                className="bg-slate-900 border-slate-700 text-white h-9 text-sm font-mono"
              />
              <Button
                data-testid="settings-link-wallet-btn"
                onClick={async () => {
                  if (!newWallet || !newWallet.startsWith('r') || newWallet.length < 25) {
                    toast.error('Enter a valid XRP address');
                    return;
                  }
                  setLinking(true);
                  try {
                    await axios.post(`${API}/auth/link-wallet`, { xrpl_address: newWallet });
                    toast.success('Wallet linked!');
                    setNewWallet('');
                    if (refreshUser) await refreshUser();
                  } catch (e) {
                    const detail = e.response?.data?.detail;
                    if (e.response?.status === 409 && detail?.can_claim) {
                      toast.warning('Wallet is linked to another account. Use the Claim flow from the Home page Link button.');
                    } else {
                      const msg = typeof detail === 'string' ? detail : (detail?.message || 'Failed to link wallet');
                      toast.error(msg);
                    }
                  } finally { setLinking(false); }
                }}
                disabled={linking}
                size="sm"
                variant="outline"
                className="w-full border-slate-600 text-slate-300 hover:bg-slate-800 h-9 text-xs"
              >
                <Link2 className="w-3.5 h-3.5 mr-1.5" /> {linking ? 'Linking…' : 'Link Manually'}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Address Book — trusted withdrawal destinations */}
      <div className="px-4 pt-5 space-y-3">
        <div className="flex items-center gap-2">
          <BookMarked className="w-4 h-4 text-emerald-400" />
          <p className="text-xs font-bold text-white uppercase tracking-wide">Address Book</p>
        </div>
        <div className="bg-slate-800/40 border border-slate-700/40 rounded-xl p-4 space-y-3">
          <p className="text-[11px] text-slate-400 leading-relaxed">
            Save trusted XRPL addresses (cold wallet, exchange, friend) so you can pick one with a tap during withdrawal — no copy/paste typos.
          </p>

          {/* Add new */}
          <div className="space-y-2">
            <Input
              data-testid="addr-label-input"
              placeholder='Label (e.g. "Cold Wallet")'
              value={addrLabel}
              maxLength={40}
              onChange={(e) => setAddrLabel(e.target.value)}
              className="bg-slate-900 border-slate-700 text-white h-9 text-sm"
            />
            <Input
              data-testid="addr-value-input"
              placeholder="rXXXXXX..."
              value={addrValue}
              onChange={(e) => setAddrValue(e.target.value.trim())}
              className="bg-slate-900 border-slate-700 text-white h-9 text-sm font-mono"
            />
            <Button
              data-testid="addr-save-btn"
              onClick={saveAddress}
              disabled={addrSaving}
              size="sm"
              className="w-full bg-emerald-600 hover:bg-emerald-500 h-9 text-xs"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> {addrSaving ? 'Saving…' : 'Save Address'}
            </Button>
          </div>

          {/* List */}
          {addresses.length === 0 ? (
            <p data-testid="addr-empty" className="text-[11px] text-slate-500 bg-slate-900/40 border border-slate-700/30 rounded-lg p-3 text-center">
              No saved addresses yet.
            </p>
          ) : (
            <div className="space-y-1.5">
              {addresses.map((a) => (
                <div
                  key={a.entry_id}
                  data-testid={`addr-row-${a.entry_id.slice(0, 8)}`}
                  className="flex items-center justify-between gap-2 bg-slate-900/50 border border-slate-700/40 rounded-lg px-3 py-2"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-white font-medium truncate">{a.label}</p>
                    <code className="text-[10px] text-slate-400 font-mono break-all block leading-tight">{a.address}</code>
                  </div>
                  <button
                    data-testid={`addr-delete-${a.entry_id.slice(0, 8)}`}
                    onClick={() => deleteAddress(a.entry_id, a.label)}
                    className="text-red-400/70 hover:text-red-400 p-1.5 -mr-1"
                    aria-label={`Delete ${a.label}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Notification Toggles */}
      <div className="px-4 pt-5 space-y-2.5">
        <div className="flex items-center gap-2">
          <Bell className="w-4 h-4 text-orange-400" />
          <p className="text-xs font-bold text-white uppercase tracking-wide">Notifications</p>
        </div>
        {toggleItems.map((item) => {
          const Icon = item.icon;
          const enabled = prefs?.[item.key] ?? true;
          return (
            <div
              key={item.key}
              data-testid={`toggle-${item.key}`}
              className="flex items-center justify-between bg-slate-800/40 border border-slate-700/40 rounded-xl p-4"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-slate-700/50 flex items-center justify-center">
                  <Icon className={`w-4.5 h-4.5 ${item.color}`} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">{item.label}</p>
                  <p className="text-[10px] text-slate-500 mt-0.5">{item.desc}</p>
                </div>
              </div>
              {/* Toggle Switch */}
              <button
                data-testid={`switch-${item.key}`}
                onClick={() => togglePref(item.key)}
                disabled={saving}
                className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${
                  enabled ? 'bg-orange-500' : 'bg-slate-600'
                }`}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200 ${
                  enabled ? 'translate-x-5' : 'translate-x-0'
                }`} />
              </button>
            </div>
          );
        })}
      </div>

      {/* Blocked Users */}
      <div className="px-4 pt-5 space-y-2">
        <p className="text-xs font-bold text-white uppercase tracking-wide">Blocked Sellers</p>
        {blocks.length === 0 ? (
          <p data-testid="blocks-empty" className="text-[11px] text-slate-500 bg-slate-800/30 border border-slate-700/30 rounded-xl p-3">
            No blocked sellers. Block a seller from their listing's overflow menu to hide their listings.
          </p>
        ) : (
          <div className="space-y-1.5">
            {blocks.map((b) => (
              <div
                key={b.blocked_user_id}
                data-testid={`block-row-${b.blocked_user_id.slice(0,8)}`}
                className="flex items-center justify-between bg-slate-800/40 border border-slate-700/40 rounded-xl px-3 py-2"
              >
                <button
                  onClick={() => navigate(`/profile/${encodeURIComponent(b.blocked_username)}`)}
                  className="text-xs text-white font-medium hover:text-indigo-300"
                >
                  @{b.blocked_username}
                </button>
                <Button
                  data-testid={`unblock-btn-${b.blocked_user_id.slice(0,8)}`}
                  onClick={() => handleUnblock(b.blocked_user_id, b.blocked_username)}
                  size="sm"
                  variant="outline"
                  className="h-7 text-[10px] border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                >
                  Unblock
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info */}
      <div className="px-4 pt-5">
        <div className="bg-slate-800/30 border border-slate-700/30 rounded-xl p-3.5">
          <div className="flex items-start gap-2.5">
            <BellOff className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-[11px] text-slate-400 leading-relaxed">
                Notifications are sent via Telegram. If you disable all alerts, you can still check results in the app.
                Win alerts cannot be fully disabled — we'll always notify you if you win!
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Sign Out */}
      <div className="px-4 pt-5">
        <Button
          data-testid="settings-signout-btn"
          onClick={() => {
            logout();
            toast.info('Signed out');
            navigate('/');
          }}
          variant="outline"
          className="w-full border-red-500/40 text-red-300 hover:bg-red-500/10 h-10 text-sm"
        >
          <LogOut className="w-4 h-4 mr-2" /> Sign Out
        </Button>
        <p className="text-[10px] text-slate-600 text-center mt-2">
          You can sign back in any time from the home page.
        </p>
      </div>

      {/* Xaman Connect (one-tap wallet linking) */}
      <XamanConnectDialog
        open={showXamanDialogSettings}
        onOpenChange={setShowXamanDialogSettings}
        onSuccess={() => { if (refreshUser) refreshUser(); }}
      />
    </div>
  );
}

export default Settings;
